import React, { useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { articles, profile } from "../mock/mock";
import { ArrowLeft, ArrowRight, Clock, Mail, Linkedin } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const idx = articles.findIndex((a) => a.slug === slug);
  const next = idx >= 0 ? articles[(idx + 1) % articles.length] : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (article) {
      document.title = `${article.title} — ${profile.name}`;
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-ivory-50 grid place-items-center px-6">
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-bronze mb-3">404</div>
          <h1 className="font-serif-display text-4xl text-ink mb-4">Article not found.</h1>
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-ink text-ivory-50 rounded-sm text-sm"
          >
            <ArrowLeft className="h-4 w-4" /> Back home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ivory-50 text-ink">
      <Header />
      <main>
        <article className="pt-32 pb-20 lg:pt-40">
          <div className="max-w-3xl mx-auto px-6">
            <Link
              to="/#insights"
              className="inline-flex items-center gap-2 text-sm text-ink/65 hover:text-bronze transition-colors mb-10"
            >
              <ArrowLeft className="h-4 w-4" /> All insights
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <span className="text-[11px] uppercase tracking-[0.22em] text-bronze font-medium">
                {article.category}
              </span>
              <span className="text-ink/30">•</span>
              <span className="text-[11px] uppercase tracking-[0.22em] text-ink/50">
                {article.date}
              </span>
              <span className="text-ink/30">•</span>
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-ink/50">
                <Clock className="h-3.5 w-3.5" /> {article.readTime}
              </span>
            </div>

            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] font-light">
              {article.title}
            </h1>

            <p className="mt-8 font-serif-display text-xl lg:text-2xl text-ink/75 leading-relaxed italic">
              {article.excerpt}
            </p>

            <div className="mt-10 flex items-center gap-4 pt-6 border-t border-ink/10">
              <div className="h-11 w-11 rounded-sm bg-ink text-ivory-50 grid place-items-center font-serif-display text-sm">
                {profile.initials}
              </div>
              <div>
                <div className="text-sm text-ink">{profile.name}</div>
                <div className="text-xs text-ink/55">{profile.title} · {profile.location}</div>
              </div>
            </div>

            <div className="mt-14 space-y-7">
              {article.body.map((p, i) => (
                <p key={i} className="text-[17px] lg:text-[18px] text-ink/85 leading-[1.8]">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-16 p-8 border border-ink/10 rounded-sm bg-ivory-100/70">
              <div className="text-[11px] uppercase tracking-[0.22em] text-bronze mb-2">
                Continue the conversation
              </div>
              <h3 className="font-serif-display text-2xl text-ink mb-4">
                Have a thought on this piece?
              </h3>
              <p className="text-sm text-ink/70 leading-relaxed mb-6">
                I write these in between running service organisations — happy to trade notes with
                fellow operators, CIOs and service leaders.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`mailto:${profile.email}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-ivory-50 rounded-sm text-sm hover:bg-ink-700 transition-colors"
                >
                  <Mail className="h-4 w-4" /> Email me
                </a>
                <a
                  href={profile.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-ink/20 rounded-sm text-sm text-ink hover:border-ink transition-colors"
                >
                  <Linkedin className="h-4 w-4" /> Connect
                </a>
              </div>
            </div>

            {next && (
              <Link
                to={`/insights/${next.slug}`}
                className="mt-14 block group border-t border-ink/10 pt-8"
              >
                <div className="text-[11px] uppercase tracking-[0.22em] text-ink/55 mb-3">
                  Up next
                </div>
                <div className="flex items-start justify-between gap-6">
                  <h4 className="font-serif-display text-2xl lg:text-3xl text-ink group-hover:text-bronze transition-colors leading-snug">
                    {next.title}
                  </h4>
                  <ArrowRight className="h-6 w-6 text-ink/40 group-hover:text-bronze group-hover:translate-x-1 transition-all flex-shrink-0 mt-2" />
                </div>
              </Link>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default ArticlePage;
